import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ToothBadgeFlags {
  hasImage: boolean;
  hasPlan: boolean;
  hasNote: boolean;
}

// ── Query Keys ────────────────────────────────────────────────────────────────

const toothBadgesQueryKey = (patientId: string | null) =>
  ['dental', 'tooth-badges', patientId] as const;

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * Returns a Map<fdi, ToothBadgeFlags> for a patient.
 * Queries tooth_images, tooth_treatment_plans, and patient_tooth_status notes.
 * Gracefully returns empty Map if any query fails.
 */
export function useToothBadgeFlags(patientId: string | null): Map<string, ToothBadgeFlags> {
  const { data } = useQuery({
    queryKey: toothBadgesQueryKey(patientId),
    enabled: Boolean(patientId),
    staleTime: 30_000,
    queryFn: async (): Promise<Map<string, ToothBadgeFlags>> => {
      if (!patientId) return new Map();

      const result = new Map<string, ToothBadgeFlags>();

      // ── tooth_images — hasImage ──────────────────────────────────────────────
      try {
        const { data: images } = await supabase
          .from('tooth_images')
          .select('tooth_fdis')
          .eq('patient_id', patientId);

        if (images) {
          for (const row of images) {
            const fdis: string[] = Array.isArray(row.tooth_fdis) ? row.tooth_fdis : [];
            for (const fdi of fdis) {
              const existing = result.get(fdi) ?? { hasImage: false, hasPlan: false, hasNote: false };
              result.set(fdi, { ...existing, hasImage: true });
            }
          }
        }
      } catch {
        // Non-critical — silently skip
      }

      // ── tooth_treatment_plans — hasPlan ──────────────────────────────────────
      try {
        const { data: plans } = await supabase
          .from('tooth_treatment_plans')
          .select('tooth_fdi')
          .eq('patient_id', patientId)
          .in('plan_status', ['proposed', 'accepted', 'scheduled']);

        if (plans) {
          for (const row of plans) {
            const fdi = row.tooth_fdi as string;
            if (!fdi) continue;
            const existing = result.get(fdi) ?? { hasImage: false, hasPlan: false, hasNote: false };
            result.set(fdi, { ...existing, hasPlan: true });
          }
        }
      } catch {
        // Non-critical — silently skip
      }

      // ── patient_tooth_status notes — hasNote ─────────────────────────────────
      try {
        const { data: statuses } = await supabase
          .from('patient_tooth_status')
          .select('tooth_fdi, notes')
          .eq('patient_id', patientId)
          .not('notes', 'is', null)
          .neq('notes', '');

        if (statuses) {
          for (const row of statuses) {
            const fdi = row.tooth_fdi as string;
            if (!fdi) continue;
            const existing = result.get(fdi) ?? { hasImage: false, hasPlan: false, hasNote: false };
            result.set(fdi, { ...existing, hasNote: true });
          }
        }
      } catch {
        // Non-critical — silently skip
      }

      return result;
    },
  });

  return data ?? new Map();
}
